import {
  ApiTab,
  ByomTab,
  EmailPreferences,
  FORUM_INBOX_LIMIT,
  GamesTab,
  InboxTab,
  NotificationTab,
  ProfileTab,
  ReviewUsageCard,
  SocialTab,
  SubscriptionTab,
} from '@/components/dashboard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth'
import { DASHBOARD_SAFETY_POLL_INTERVAL_MS } from '@/lib/dashboard-polling'
import {
  type DashboardBadgeCounts,
  DASHBOARD_TAB_LABELS,
  type DashboardTab,
  dashboardTabBadgeCount,
  dashboardTabForHost,
  dashboardTabsForHost,
  formatBadgeCount,
} from '@/lib/dashboard-tabs'
import { useI18n } from '@/lib/i18n'
import { useGameAttention } from '@/lib/game-attention'
import { useTRPC } from '@/lib/trpc'
import { useSocialOverview } from '@/social/social-provider'
import type { SocialOverview } from '@/social/types'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

export function DashboardPage({
  notificationSetupEnabled = false,
}: {
  notificationSetupEnabled?: boolean
}) {
  const { user, authResolved } = useAuth()
  const gameAttention = useGameAttention()
  const { t } = useI18n()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const dashboardTabs = dashboardTabsForHost(notificationSetupEnabled)
  const requestedTab = dashboardTabForHost(tabParam, notificationSetupEnabled)
  const activeTab
    = tabParam === 'creditHistory'
      ? 'subscription'
      : requestedTab ?? 'subscription'

  const handleTabChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', value)
    setSearchParams(nextParams)
  }

  const { data: gamesData } = useQuery({
    ...trpc.userRouter.getUserGames.queryOptions({ gameType: 'game' }),
    enabled: authResolved && !!user,
    refetchOnWindowFocus: true,
    refetchInterval: DASHBOARD_SAFETY_POLL_INTERVAL_MS,
  })

  const { data: forumNotificationsData } = useQuery({
    ...trpc.forumRouter.listNotifications.queryOptions({
      limit: FORUM_INBOX_LIMIT,
    }),
    enabled: authResolved && !!user,
    refetchInterval: 30_000,
  })
  const registeredUser = !!user && !user.isAnonymous
  const socialOverviewQuery = useSocialOverview(registeredUser)
  const socialOverview = socialOverviewQuery.data as SocialOverview | undefined
  const badgeCounts: DashboardBadgeCounts = {
    inbox: authResolved && user ? forumNotificationsData?.unreadCount ?? 0 : 0,
    social: authResolved && user
      ? (socialOverview?.counts.unread ?? 0)
      + (socialOverview?.counts.incoming ?? 0)
      : 0,
    games: gameAttention.awaitingCount,
  }

  const renderTabLabel = (tab: DashboardTab) => {
    const label = t(DASHBOARD_TAB_LABELS[tab])
    const badgeCount = dashboardTabBadgeCount(tab, badgeCounts)
    if (badgeCount <= 0) {
      return label
    }

    return (
      <span className="flex w-full items-center justify-between gap-2">
        <span>{label}</span>
        <Badge
          className="h-5 min-w-5 rounded-full px-1 text-[10px] leading-none"
          data-testid={tab === 'games' ? 'dashboard-games-badge' : undefined}
          aria-label={tab === 'games' ? `${t('gameAttention.awaitingInput')}: ${badgeCount}` : undefined}
        >
          {formatBadgeCount(badgeCount)}
        </Badge>
      </span>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1920px] px-3 pb-8 pt-20 sm:px-4 md:px-8 md:pt-24 lg:px-12">
      <div className="mb-4 flex items-start justify-between gap-3 md:mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold md:text-3xl">
            {t('auth.dashboard.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            {t('auth.dashboard.description')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <X className="size-4" />
            <span className="sr-only">{t('common.close')}</span>
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="grid gap-4 lg:grid-cols-[200px_1fr] lg:gap-6">
          {
          }
          <div className="hidden md:block lg:hidden">
            <Select value={activeTab} onValueChange={handleTabChange}>
              <SelectTrigger
                className="h-11 w-full bg-background"
                data-testid="dashboard-mobile-tab-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dashboardTabs.map(tab => (
                  <SelectItem key={tab} value={tab}>
                    {renderTabLabel(tab)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="hidden lg:block">
            <TabsList className="flex h-auto w-full flex-col lg:w-auto lg:items-start">
              {dashboardTabs.map(tab => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="w-full justify-start"
                >
                  {renderTabLabel(tab)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="min-w-0">
            <TabsContent value="inbox" className="mt-0">
              <InboxTab />
            </TabsContent>

            <TabsContent value="social" className="mt-0">
              <SocialTab />
            </TabsContent>

            <TabsContent value="profile" className="mt-0 space-y-6">
              <ProfileTab />
              <EmailPreferences />
            </TabsContent>

            {notificationSetupEnabled && (
              <TabsContent value="notifications" className="mt-0">
                <NotificationTab />
              </TabsContent>
            )}

            <TabsContent value="subscription" className="mt-0">
              <SubscriptionTab />
            </TabsContent>

            <TabsContent
              value="games"
              className="mt-0"
              data-testid="dashboard-games-tab-content"
            >
              <GamesTab
                title={t('auth.dashboard.gamesTitle')}
                description={t('auth.dashboard.gamesDescription')}
                games={authResolved && user ? gamesData?.games : undefined}
              />
            </TabsContent>

            <TabsContent
              value="reviews"
              className="mt-0"
              data-testid="dashboard-reviews-tab-content"
            >
              <ReviewUsageCard />
            </TabsContent>

            <TabsContent value="api" className="mt-0">
              <ApiTab />
            </TabsContent>

            <TabsContent value="byom" className="mt-0">
              <ByomTab />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  )
}
